import { customElement, property, query } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_NSX } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import { getNsxSearchableKeywords, NSX_DEFAULT_VALUE } from '../../../../utils/uiSettings/nsx/nsx.values';
import { NsxFormValue, NsxSearchableKeywords } from '../../../../utils/uiSettings/nsx/nsx.types';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { html, TemplateResult, unsafeCSS } from 'lit';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import _ from 'lodash';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { selectPoliciesState } from '../../../../redux/slice';
import { store } from '@nirvana/redux/store';
import { getPhantom } from '../../../../redux/actions';
import deepmerge from 'deepmerge';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { InputWidthValues } from '../../../../../../../ui/gzInput';
import {
  NsxApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { MaxLengthValidator } from 'apps/webcomponents/src/ui/gzForm/validators/max-length.validator';
import { RemoteValidator } from 'apps/webcomponents/src/ui/gzForm/validators/remote.validator';
import { of } from 'rxjs';
import { getNavigationHeaderSlice } from '../../../../redux/navigationHeaderSlice/actions.navigation';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { GzHardCodedFormField } from 'apps/webcomponents/src/ui/gzForm/formField/component/GzHardCodedFormField';
import { RegexValidator } from '../../../../../../../ui/gzForm/validators/regex.validator';

@customElement(WEB_COMPONENT_POLICY_PANEL_NSX)
export class GzNsxWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  NsxFormValue,
  NsxSearchableKeywords,
  NsxApiClients
> {
  protected path = {
    moduleId: ModuleId.NSX,
    sectionId: SectionId.NONE,
  };

  protected searchableKeywords = getNsxSearchableKeywords();
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  @query('#nsx-toggle')
  private nsxToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#name-form-field')
  private nameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#name-input')
  private nameInput: GzInputWebComponent;

  @query('#nsx-info-text')
  private nsxInfoText: HTMLElement;
  @query('#nsx-content')
  private nsxContent: HTMLElement;

  private defaultFormField: GzHardCodedFormField;

  @property({ attribute: true, reflect: true })
  private nsxBaseStateEnabled: boolean;
  @property({ attribute: true, reflect: true })
  private currentNsxState: boolean;
  @property()
  private nsxBaseName?: string;

  private isPolicyInUpdate() {
    const policyState = selectPoliciesState(store.getState());
    return !getPhantom(policyState);
  }

  private isEditingPolicyNameDisabled() {
    return this.isPolicyInUpdate() && this.nsxBaseStateEnabled;
  }

  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency('Nsx') as NsxApiClients;
  }

  protected getPolicyId() {
    const policyId = this.isPolicyInUpdate() ? PolicyPanelManager.getInstance().getPolicyId() : null;
    if (policyId === '') {
      return null;
    }
    return policyId;
  }

  protected setValidators() {
    this.nameFormField.Validators = [
      RequiredValidator(translateFn('Required field')),
      MaxLengthValidator(80, translateFn('The name must have a maximum of {maxLength} characters.', { maxLength: 80 })),
      RegexValidator(/^[^\s][a-zA-Z0-9_]*/i, translateFn('Invalid policy name')),
      RemoteValidator(
        (value: string) => of(this.clients.isNsxPolicyValid(value, this.getPolicyId())),
        (response: { success: boolean }) => response.success,
        translateFn('The policy name is invalid or already exists.'),
        translateFn('Connection error')
      ),
    ];
  }

  protected createFormGroup(): void {
    this.defaultFormField = new GzHardCodedFormField<boolean>(false);

    this.formGroup = FormGroup.build({
      uiSettings: {
        nsx: {
          enabled: this.nsxToggle,
          name: this.nameFormField,
          default: this.defaultFormField,
        },
      },
    });
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);
    const nsxValue = _.get(value, ['uiSettings', 'nsx'], {});

    if (USE_EXT_JS_VERSION) {
      return nsxValue;
    }

    return value;
  }

  private getNsxDefaultPolicyName(): string {
    const policyState = selectPoliciesState(store.getState());
    const navigationHeaderSlice = getNavigationHeaderSlice(policyState);
    const policyName = navigationHeaderSlice.policyName;
    // replace non-letters with _
    let nsxPolicyName = policyName.replace(/[^a-zA-Z0-9_]/g, '_');

    // replace multiple _ with single
    nsxPolicyName = nsxPolicyName.replace(/_+/g, '_');

    // trim _ left
    nsxPolicyName = nsxPolicyName.replace(/^[_]+$/, '');

    // trim _ right
    nsxPolicyName = nsxPolicyName.replace(/[_]+$/, '');

    return nsxPolicyName;
  }

  protected getValueFromManager() {
    const defaultValue = NSX_DEFAULT_VALUE;
    const value = PolicyPanelManager.getInstance().getPolicy('uiSettings.nsx');
    const returnedValue = deepmerge(defaultValue, value);
    return {
      uiSettings: {
        [ModuleId.NSX]: returnedValue,
      },
    };
  }

  protected renderContent(): TemplateResult {
    return html` <div id="nsx-content" class="nsx-content">
      <gz-form-field id="name-form-field" label="${translateFn('Policy name in NSX')}" class="nsx-name-form" required>
        <gz-input id="name-input" gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-text id="nsx-name-input" fontSize="12" fontWeight="400" class="infoText">
        ${translateFn('After the policy is saved, this field becomes read-only.')}
      </gz-text>
    </div>`;
  }

  protected renderTitle() {
    return html` <gz-form-field id="nsx-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[NsxSearchableKeywords.Nsx].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[NsxSearchableKeywords.Nsx].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html`<gz-text id="nsx-info-text" fontSize="12" fontWeight="400" class="infoText">
      ${translateFn('In this section, you can establish the policy to be used as a security profile in NSX. ')}
    </gz-text>`;
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    // set base state
    this.nsxBaseStateEnabled = PolicyPanelManager.getInstance().getAdditionalPolicyInfo([
      'valueHistory',
      'nsx',
      'enabled',
    ]);

    this.nameFormField.readonly = this.isEditingPolicyNameDisabled();
    this.currentNsxState = this.valueT.uiSettings.nsx.enabled;
    // policy in create
    if (!this.isPolicyInUpdate()) {
      this.nsxBaseName = this.valueT.uiSettings.nsx.enabled ? this.getNsxDefaultPolicyName() : '';
      this.formGroup.value = {
        uiSettings: {
          nsx: {
            enabled: this.valueT.uiSettings.nsx.enabled,
            name: this.nsxBaseName,
          },
        },
      };
      return;
    }

    // policy in update
    let nsxName = '';
    if (this.nsxBaseStateEnabled) {
      nsxName = this.valueT.uiSettings.nsx.name ?? '';
    }

    if (!this.nsxBaseStateEnabled && this.valueT.uiSettings.nsx.enabled) {
      nsxName = this.valueT.uiSettings.nsx.name ?? '';
    }

    this.nsxBaseName = nsxName;
    this.formGroup.value = {
      uiSettings: {
        nsx: {
          enabled: this.valueT.uiSettings.nsx.enabled,
          name: nsxName,
        },
      },
    };
  }

  onToggleChange(event: CustomEvent) {
    if (event.detail.checked) {
      if (!this.nsxBaseName && !this.currentNsxState) {
        this.nsxBaseName = this.getNsxDefaultPolicyName();
      }
      this.nameFormField.value = this.nsxBaseName ?? '';

      this.nameFormField.touched = true;
      this.nameFormField.readonly = this.isEditingPolicyNameDisabled();
    } else {
      this.nameFormField.readonly = true;
      this.nameInput.value = null;
    }
    this.currentNsxState = event.detail.checked;
    toggleDisabled(this.nsxInfoText, !event.detail.checked);
    toggleDisabled(this.nsxContent, !event.detail.checked);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT__POLICY_PANEL_NSX: GzNsxWebComponent;
  }
}
